import { DocumentNumberBoPart, propertyChangedJoinPointFilter } from '@skylab-shared/business';
import { BO, BusinessObjectBase, PropertyChangedJoinPoint, QueryBus, Weaver } from '@skylab-shared/core';
import { InventoryEffect, InventoryStocktakingDocumentItemModel, InventoryStocktakingDocumentModel } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding } from '@skylab-shared/utils';
import { InventoryItemQuantityPrecision } from './utils/inventory-document-item-utils';
import { InventoryDocumentItemPriceCannotBeLessThanZero } from './violations/inventory-document-item-price-cannot-be-less-than-zero-error';
import { InventoryStocktakingQuantityCannotBeLessThanZero } from './violations/inventory-stocktaking-quantity-cannot-be-less-than-zero';

@BO({
    modelType: InventoryStocktakingDocumentModel
})
export class InventoryStocktakingDocumentBO extends BusinessObjectBase<InventoryStocktakingDocumentModel> {
    public constructor(
        weaver: Weaver,
        protected readonly queryBus: QueryBus,
    ) {
        super(weaver);
        this.initBOParts();
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        this.registerQuantitiesChanges();
        this.registerReadonlyRules();
        this.registerValidationRules();
    }

    private initBOParts(): void {
        this.parts.push(...[
            new DocumentNumberBoPart(this.weaver, this.queryBus)
        ]);
    }

    private registerQuantitiesChanges(): void {
        const calcQuantity = (calc: () => number) => Calc.round(calc(), InventoryItemQuantityPrecision, MidpointRounding.AwayFromZero);
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(InventoryStocktakingDocumentItemModel, ['stocktakingQuantity'])(
            ctx => {
                const model = ctx.model as InventoryStocktakingDocumentItemModel;

                model.issueQuantity = model.receiptQuantity = 0;
                if (model.stocktakingQuantity > model.systemQuantity) model.receiptQuantity = calcQuantity(() => model.stocktakingQuantity - model.systemQuantity);
                if (model.stocktakingQuantity < model.systemQuantity) model.issueQuantity = calcQuantity(() => model.systemQuantity - model.stocktakingQuantity);;
            }
        )));
    }

    private registerReadonlyRules(): void {
        const modelType = InventoryStocktakingDocumentModel;

        this.readonly.setAsReadonly([modelType, 'issueDate']);
        this.readonly.setAsReadonly([modelType, 'inventoryEffect']);
        this.readonly.setAsReadonly([modelType, 'inventoryId']);

        this.readonly.addRule([
            [modelType, 'inventoryId'],
            [modelType, 'issueDate'],
            [modelType, 'approver'],
            [modelType, 'preparer'],
            [modelType, 'numberingId'],
            [modelType, 'documentNumber']
        ], () => this.model.inventoryEffect === InventoryEffect.Realized);

        if (this.model.inventoryEffect === InventoryEffect.Realized) {
            this.readonly.setAsReadonly([InventoryStocktakingDocumentItemModel, 'salePrice']);
            this.readonly.setAsReadonly([InventoryStocktakingDocumentItemModel, 'stocktakingQuantity']);
            this.readonly.setAsReadonly([InventoryStocktakingDocumentItemModel, 'purchasePrice']);
        }
    }

    private registerValidationRules(): void {
        this.validation.addRule(
            [[InventoryStocktakingDocumentItemModel, 'salePrice']],
            ctx => {
                return ctx.source.salePrice >= 0;
            },
            InventoryDocumentItemPriceCannotBeLessThanZero
        );

        this.validation.addRule(
            [[InventoryStocktakingDocumentItemModel, 'purchasePrice']],
            ctx => {
                return ctx.source.purchasePrice >= 0;
            },
            InventoryDocumentItemPriceCannotBeLessThanZero
        );

        this.validation.addRule(
            [[InventoryStocktakingDocumentItemModel, 'stocktakingQuantity']],
            ctx => {
                return ctx.source.stocktakingQuantity >= 0;
            },
            InventoryStocktakingQuantityCannotBeLessThanZero
        );
    }
}
